// player/SettingsMenu.tsx — the player's ⋮ "Playback settings" panel (1.27.50, owner
// concept video_watch_page_v2). Lives INSIDE the player frame so it still shows in
// fullscreen. One main list and a sub-page per setting with a Back row; Escape steps back,
// then closes, and focus returns to the ⋮ button.
//
// Every row does something real. Captions (1.27.60) shows only when the video has caption
// tracks — absent rather than a row that does nothing.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { Icon, type IconName } from '@/icons';
import { MetaRow } from '@/ui';
import { formatClock } from './chapters';
import {
  SPEED_MAX,
  SPEED_MIN,
  SPEED_PRESETS,
  usePlayerPrefs,
  type CaptionSize,
  type Quality,
  type VideoFit,
} from './playerPrefs';
import type { CaptionTrack } from '@/core/models/post';
import {
  SHORTCUTS,
  SLEEP_CHOICES,
  sleepLabel,
  type RepeatMode,
  type SleepChoice,
} from './playback';

const INK = '#fff';
const MUTED = 'rgba(255, 255, 255, 0.66)';

const Panel = styled.div`
  position: absolute;
  right: 12px;
  bottom: 64px;
  z-index: 5;
  width: min(340px, calc(100% - 24px));
  max-height: calc(100% - 80px);
  overflow-y: auto;
  padding: 8px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(14, 11, 24, 0.94);
  backdrop-filter: blur(12px);
  color: ${INK};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45);
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px 8px;
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const Group = styled.div`
  padding: 4px 0;
  & + & {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const GroupLabel = styled.div`
  padding: 6px 10px 4px;
  font-size: 11px;
  font-weight: 700;
  color: ${MUTED};
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 38px;
  padding: 6px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${INK};
  font: inherit;
  text-align: left;
  cursor: pointer;
  &:hover {
    background: rgba(255, 255, 255, 0.09);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
  &[aria-checked='true'] {
    font-weight: 700;
  }
`;

const RowLabel = styled.span`
  flex: 1;
  min-width: 0;
`;

const RowValue = styled.span`
  color: ${MUTED};
  font-variant-numeric: tabular-nums;
`;

const IconIconBtn = styled.button`
  display: inline-grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${INK};
  cursor: pointer;
  &:hover {
    background: rgba(255, 255, 255, 0.09);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
  }
`;

const SliderRow = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 10px;
  span {
    display: flex;
    justify-content: space-between;
  }
  input[type='range'] {
    width: 100%;
    accent-color: ${({ theme }) => theme.color.accentCyan};
  }
`;

const Toggle = styled.span<{ $on: boolean }>`
  position: relative;
  width: 34px;
  height: 20px;
  border-radius: 999px;
  background: ${({ $on, theme }) => ($on ? theme.color.accentCyan : 'rgba(255,255,255,0.25)')};
  flex-shrink: 0;
  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${({ $on }) => ($on ? 17 : 3)}px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    transition: left 120ms ease;
  }
`;

const Note = styled.p`
  margin: 4px 10px 8px;
  color: ${MUTED};
  font-size: 12px;
  line-height: 1.45;
`;

const Grid = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px 14px;
  margin: 4px 10px 8px;
  dt {
    color: ${MUTED};
  }
  dd {
    margin: 0;
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }
`;

const Kbd = styled.kbd`
  font: 600 12px ${({ theme }) => theme.font.mono};
  color: ${INK};
`;

export interface PlaybackInfo {
  playing: string;
  resolution: string;
  duration: number;
  bufferedAhead: number;
  droppedFrames: string;
  viewport: string;
  codecs: string;
}

export interface SettingsMenuProps {
  id: string;
  /** Open straight on a sub-page (the speed pill opens "Playback speed"). */
  startView?: 'main' | 'speed';
  hasHd: boolean;
  hdLabel: string;
  standardLabel: string;
  /** What "Auto" is playing right now. */
  autoPlaying: 'full' | 'light';
  repeat: RepeatMode;
  onRepeat: (mode: RepeatMode) => void;
  ab: { a: number | null; b: number | null };
  onSetA: () => void;
  onSetB: () => void;
  sleep: SleepChoice;
  sleepRemaining: number | null;
  onSleep: (choice: SleepChoice) => void;
  autoplayNext?: boolean;
  onAutoplayNext?: (next: boolean) => void;
  /** 1.27.60: this video's caption tracks, and the one that shows when captions are on. */
  captionTracks?: CaptionTrack[];
  captionTrack?: CaptionTrack | null;
  getInfo: () => PlaybackInfo;
  onClose: () => void;
}

type View =
  | 'main'
  | 'quality'
  | 'speed'
  | 'repeat'
  | 'sleep'
  | 'audio'
  | 'captions'
  | 'fit'
  | 'picture'
  | 'controls'
  | 'shortcuts'
  | 'info';

const TITLES: Record<View, string> = {
  main: 'Playback settings',
  quality: 'Quality',
  speed: 'Playback speed',
  repeat: 'Repeat',
  sleep: 'Sleep timer',
  audio: 'Audio & sound',
  captions: 'Captions',
  fit: 'Video fit',
  picture: 'Picture & appearance',
  controls: 'Controls & accessibility',
  shortcuts: 'Keyboard shortcuts',
  info: 'Playback information',
};

function speedLabel(s: number) {
  return s === 1 ? 'Normal' : `${Number(s.toFixed(2))}×`;
}

export function SettingsMenu(props: SettingsMenuProps) {
  const { prefs, set, reset } = usePlayerPrefs();
  const [view, setView] = useState<View>(props.startView ?? 'main');
  const [info, setInfo] = useState<PlaybackInfo | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus the first control of each page as it opens.
  useEffect(() => {
    const first = panelRef.current?.querySelector<HTMLElement>('button, input');
    first?.focus();
  }, [view]);

  // Playback information refreshes while its page is open.
  const { getInfo } = props;
  useEffect(() => {
    if (view !== 'info') return;
    const tick = () => setInfo(getInfo());
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [view, getInfo]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    if (view === 'main') props.onClose();
    else setView('main');
  }

  const autoLabel = (
    <MetaRow>
      <span>Auto</span>
      <span>{props.autoPlaying === 'full' ? props.hdLabel : props.standardLabel}</span>
    </MetaRow>
  );
  const qualityValue =
    prefs.quality === 'auto'
      ? autoLabel
      : prefs.quality === 'hd'
        ? props.hdLabel
        : props.standardLabel;
  const repeatValue =
    props.repeat === 'off' ? 'Off' : props.repeat === 'loop' ? 'Loop video' : 'A–B';
  const sleepValue =
    props.sleep === 'off'
      ? 'Off'
      : props.sleep === 'end'
        ? 'End of video'
        : props.sleepRemaining !== null
          ? formatClock(props.sleepRemaining)
          : sleepLabel(props.sleep);

  function nav(icon: IconName, label: string, to: View, value?: ReactNode) {
    return (
      <Row type="button" onClick={() => setView(to)}>
        <Icon name={icon} size={17} />
        <RowLabel>{label}</RowLabel>
        {value !== undefined && <RowValue>{value}</RowValue>}
        <Icon name="chevron-right" size={16} />
      </Row>
    );
  }

  function choice<T>(label: ReactNode, value: T, current: T, onPick: (v: T) => void) {
    const on = value === current;
    return (
      <Row
        key={String(value)}
        type="button"
        role="menuitemradio"
        aria-checked={on}
        onClick={() => onPick(value)}
      >
        <span style={{ width: 17, display: 'inline-flex' }}>
          {on && <Icon name="check" size={16} />}
        </span>
        <RowLabel>{label}</RowLabel>
      </Row>
    );
  }

  function toggle(label: string, on: boolean, onFlip: () => void, hint?: string) {
    return (
      <Row type="button" role="menuitemcheckbox" aria-checked={on} onClick={onFlip}>
        <RowLabel>
          {label}
          {hint && (
            <Note as="span" style={{ display: 'block', margin: 0 }}>
              {hint}
            </Note>
          )}
        </RowLabel>
        <Toggle $on={on} aria-hidden="true" />
      </Row>
    );
  }

  return (
    <Panel
      ref={panelRef}
      id={props.id}
      data-player-menu
      role="dialog"
      aria-label={TITLES[view]}
      onKeyDown={onKeyDown}
      data-testid="player-settings"
    >
      <Head>
        {view !== 'main' ? (
          <IconIconBtn type="button" aria-label="Back" onClick={() => setView('main')}>
            <Icon name="chevron-left" size={18} />
          </IconIconBtn>
        ) : null}
        <span style={{ flex: 1 }}>{TITLES[view]}</span>
        <IconIconBtn type="button" aria-label="Close settings" onClick={props.onClose}>
          <Icon name="close" size={16} />
        </IconIconBtn>
      </Head>

      {view === 'main' && (
        <>
          <Group>
            <GroupLabel>Playback</GroupLabel>
            {props.hasHd && nav('settings', 'Quality', 'quality', qualityValue)}
            {nav('gauge', 'Playback speed', 'speed', speedLabel(prefs.speed))}
            {nav('repeat', 'Repeat', 'repeat', repeatValue)}
            {nav('timer', 'Sleep timer', 'sleep', sleepValue)}
            {props.onAutoplayNext &&
              toggle('Autoplay next', Boolean(props.autoplayNext), () =>
                props.onAutoplayNext?.(!props.autoplayNext),
              )}
          </Group>
          <Group>
            <GroupLabel>{props.captionTrack ? 'Audio & captions' : 'Audio'}</GroupLabel>
            {nav('volume-2', 'Audio & sound', 'audio', prefs.muted ? 'Muted' : 'Original')}
            {props.captionTrack &&
              nav(
                'captions',
                'Captions',
                'captions',
                prefs.captionsOn ? props.captionTrack.label : 'Off',
              )}
          </Group>
          <Group>
            <GroupLabel>Viewing</GroupLabel>
            {nav('crop', 'Video fit', 'fit', prefs.fit === 'fit' ? 'Fit' : 'Fill')}
            {nav('palette', 'Picture & appearance', 'picture')}
            {nav('accessibility', 'Controls & accessibility', 'controls')}
          </Group>
          <Group>
            <GroupLabel>Tools</GroupLabel>
            {nav('keyboard', 'Keyboard shortcuts', 'shortcuts')}
            {nav('info', 'Playback information', 'info')}
            <Row type="button" onClick={() => reset()}>
              <Icon name="refresh" size={17} />
              <RowLabel>Reset player preferences</RowLabel>
            </Row>
          </Group>
        </>
      )}

      {view === 'quality' && (
        <Group role="menu" aria-label="Quality">
          {choice<Quality>(autoLabel, 'auto', prefs.quality, (q) => set({ quality: q }))}
          {choice<Quality>(props.hdLabel, 'hd', prefs.quality, (q) => set({ quality: q }))}
          {choice<Quality>(props.standardLabel, 'standard', prefs.quality, (q) =>
            set({ quality: q }),
          )}
          <Note>
            Auto plays the sharper copy when the player is big enough to show it and your connection
            isn’t in data-saver mode.
          </Note>
        </Group>
      )}

      {view === 'speed' && (
        <Group role="menu" aria-label="Playback speed">
          {SPEED_PRESETS.map((s) =>
            choice(speedLabel(s), s, prefs.speed, (v) => set({ speed: v })),
          )}
          <SliderRow>
            <span>
              Custom <RowValue>{Number(prefs.speed.toFixed(2))}×</RowValue>
            </span>
            <input
              type="range"
              min={SPEED_MIN}
              max={SPEED_MAX}
              step={0.05}
              value={prefs.speed}
              aria-label="Custom speed"
              onChange={(e) => set({ speed: Number(e.target.value) })}
            />
          </SliderRow>
          {toggle('Preserve pitch', prefs.preservePitch, () =>
            set({ preservePitch: !prefs.preservePitch }),
          )}
        </Group>
      )}

      {view === 'repeat' && (
        <Group role="menu" aria-label="Repeat">
          {choice<RepeatMode>('Off', 'off', props.repeat, props.onRepeat)}
          {choice<RepeatMode>('Loop video', 'loop', props.repeat, props.onRepeat)}
          {choice<RepeatMode>('A–B repeat', 'ab', props.repeat, props.onRepeat)}
          {props.repeat === 'ab' && (
            <>
              <Row type="button" onClick={props.onSetA}>
                <RowLabel>Set A here</RowLabel>
                <RowValue>{props.ab.a === null ? 'Not set' : formatClock(props.ab.a)}</RowValue>
              </Row>
              <Row type="button" onClick={props.onSetB}>
                <RowLabel>Set B here</RowLabel>
                <RowValue>{props.ab.b === null ? 'Not set' : formatClock(props.ab.b)}</RowValue>
              </Row>
              <Note>Plays from A to B over and over. Set A, let it play, then set B.</Note>
            </>
          )}
        </Group>
      )}

      {view === 'sleep' && (
        <Group role="menu" aria-label="Sleep timer">
          {SLEEP_CHOICES.map((c) => choice(sleepLabel(c), c, props.sleep, props.onSleep))}
          <Note>Pauses the video when the time is up.</Note>
        </Group>
      )}

      {view === 'audio' && (
        <Group>
          <Row type="button" role="menuitemradio" aria-checked disabled>
            <span style={{ width: 17, display: 'inline-flex' }}>
              <Icon name="check" size={16} />
            </span>
            <RowLabel>Original audio</RowLabel>
          </Row>
          <SliderRow>
            <span>
              Volume <RowValue>{Math.round(prefs.volume * 100)}%</RowValue>
            </span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={prefs.volume}
              aria-label="Volume"
              onChange={(e) => set({ volume: Number(e.target.value), muted: false })}
            />
          </SliderRow>
          {toggle('Mute', prefs.muted, () => set({ muted: !prefs.muted }))}
          <Note>Videos on TruXSocial have one audio track, as uploaded.</Note>
        </Group>
      )}

      {view === 'captions' && (
        <>
          <Group role="menu" aria-label="Caption track">
            {choice('Off', 'off', prefs.captionsOn ? 'track' : 'off', () =>
              set({ captionsOn: false }),
            )}
            {(props.captionTracks ?? []).map((t) =>
              choice(
                t.source === 'auto' ? `${t.label} (made automatically)` : t.label,
                t.language,
                prefs.captionsOn ? props.captionTrack?.language : 'off',
                () => set({ captionsOn: true, captionLang: t.language }),
              ),
            )}
          </Group>
          <Group role="menu" aria-label="Caption size">
            <GroupLabel>Size</GroupLabel>
            {(['small', 'medium', 'large'] as CaptionSize[]).map((size) =>
              choice(
                size === 'small' ? 'Small' : size === 'medium' ? 'Medium' : 'Large',
                size,
                prefs.captionSize,
                (v) => set({ captionSize: v }),
              ),
            )}
          </Group>
          {toggle(
            'Background behind captions',
            prefs.captionBackground,
            () => set({ captionBackground: !prefs.captionBackground }),
            'Off draws an outline around the letters instead.',
          )}
        </>
      )}

      {view === 'fit' && (
        <Group role="menu" aria-label="Video fit">
          {choice<VideoFit>('Fit: show the whole picture', 'fit', prefs.fit, (f) =>
            set({ fit: f }),
          )}
          {choice<VideoFit>('Fill: crop to fill the player', 'fill', prefs.fit, (f) =>
            set({ fit: f }),
          )}
        </Group>
      )}

      {view === 'picture' && (
        <Group>
          <SliderRow>
            <span>
              Brightness <RowValue>{Math.round(prefs.brightness * 100)}%</RowValue>
            </span>
            <input
              type="range"
              min={0.5}
              max={1.5}
              step={0.05}
              value={prefs.brightness}
              aria-label="Brightness"
              onChange={(e) => set({ brightness: Number(e.target.value) })}
            />
          </SliderRow>
          <SliderRow>
            <span>
              Contrast <RowValue>{Math.round(prefs.contrast * 100)}%</RowValue>
            </span>
            <input
              type="range"
              min={0.5}
              max={1.5}
              step={0.05}
              value={prefs.contrast}
              aria-label="Contrast"
              onChange={(e) => set({ contrast: Number(e.target.value) })}
            />
          </SliderRow>
          {toggle(
            'Ambient light',
            prefs.ambient,
            () => set({ ambient: !prefs.ambient }),
            'A soft glow of the video’s colours around the player.',
          )}
        </Group>
      )}

      {view === 'controls' && (
        <Group>
          {toggle('Always show controls', prefs.alwaysShowControls, () =>
            set({ alwaysShowControls: !prefs.alwaysShowControls }),
          )}
          {toggle('Larger controls', prefs.largeControls, () =>
            set({ largeControls: !prefs.largeControls }),
          )}
        </Group>
      )}

      {view === 'shortcuts' && (
        <Grid aria-label="Keyboard shortcuts">
          {SHORTCUTS.map((s) => (
            <Fragmentish key={s.keys} keys={s.keys} action={s.action} />
          ))}
        </Grid>
      )}

      {view === 'info' && info && (
        <Grid aria-label="Playback information">
          <dt>Playing</dt>
          <dd>{info.playing}</dd>
          <dt>Resolution</dt>
          <dd>{info.resolution}</dd>
          <dt>Codecs</dt>
          <dd>{info.codecs}</dd>
          <dt>Length</dt>
          <dd>{formatClock(info.duration)}</dd>
          <dt>Buffered ahead</dt>
          <dd>{info.bufferedAhead.toFixed(1)} s</dd>
          <dt>Dropped frames</dt>
          <dd>{info.droppedFrames}</dd>
          <dt>Player size</dt>
          <dd>{info.viewport}</dd>
        </Grid>
      )}
    </Panel>
  );
}

function Fragmentish({ keys, action }: { keys: string; action: string }) {
  return (
    <>
      <dt>
        <Kbd>{keys}</Kbd>
      </dt>
      <dd>{action}</dd>
    </>
  );
}
